import { Link } from "@/lib/link";
import { Linkedin, Instagram, Youtube, Facebook, MessageCircle, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { postForm } from "../../lib/api";
import { FORMS } from "../../constants/testIds";
import { productCategories } from "../../data/products";
import { industries } from "../../data/industries";

export default function Footer() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await postForm("/newsletter", { email });
            toast.success(t("toast.subscribed"));
            setEmail("");
        } catch (err) {
            toast.error(t("toast.subscribeError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-[#061A2E] text-white/80 pt-20 pb-10 border-t border-white/10">
            <div className="container-x grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
                <div className="lg:col-span-2">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-white to-slate-200 flex items-center justify-center shadow-lg shadow-slate-950/20">
                            <span className="text-[#0A2540] font-extrabold text-lg">E</span>
                        </div>
                        <div className="font-extrabold text-lg text-white tracking-tight">
                            Exportfy<span className="text-[#10B981]">.world</span>
                        </div>
                    </Link>
                    <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-md">
                        {t("footer.description")}
                    </p>

                    <div className="mt-6 space-y-2 text-sm text-white/70">
                        <a href="https://wa.me/919918000070" className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4" /> +91 99180 00070</a>
                        <a href="mailto:hello@exportfy.world" className="flex items-center gap-2 hover:text-white"><Mail className="w-4 h-4" /> hello@exportfy.world</a>
                        <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /> Panipat · New Delhi · India</div>
                    </div>

                    <div className="mt-6 flex items-center gap-3">
                        {[
                            { Icon: Linkedin, href: "#", label: "LinkedIn" },
                            { Icon: Instagram, href: "#", label: "Instagram" },
                            { Icon: Youtube, href: "#", label: "YouTube" },
                            { Icon: Facebook, href: "#", label: "Facebook" },
                            { Icon: MessageCircle, href: "https://wa.me/919918000070", label: "WhatsApp" },
                            { Icon: Twitter, href: "#", label: "X" },
                        ].map(({ Icon, href, label }) => (
                            <a key={label} href={href} aria-label={label} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition">
                                <Icon className="w-4 h-4" strokeWidth={1.75} />
                            </a>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="overline text-white/50 mb-4">{t("footer.products")}</div>
                    <ul className="space-y-2.5 text-sm">
                        {productCategories.slice(0, 6).map((c) => (
                            <li key={c.slug}>
                                <Link to={`/products/${c.slug}`} className="text-white/70 hover:text-white">{c.name}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="overline text-white/50 mb-4">{t("footer.industries")}</div>
                    <ul className="space-y-2.5 text-sm">
                        {industries.slice(0, 7).map((i) => (
                            <li key={i.slug}>
                                <Link to="/industries" className="text-white/70 hover:text-white">{i.name}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="overline text-white/50 mb-4">{t("footer.company")}</div>
                    <ul className="space-y-2.5 text-sm">
                        <li><Link to="/about" className="text-white/70 hover:text-white">{t("footer.aboutExportfy")}</Link></li>
                        <li><Link to="/export-solutions" className="text-white/70 hover:text-white">{t("nav.solutions")}</Link></li>
                        <li><Link to="/custom-sourcing" className="text-white/70 hover:text-white">{t("nav.customSourcing")}</Link></li>
                        <li><Link to="/import-business" className="text-white/70 hover:text-white">{t("nav.importBusiness")}</Link></li>
                        <li><Link to="/global-partner" className="text-white/70 hover:text-white">{t("nav.partner")}</Link></li>
                        <li><Link to="/government" className="text-white/70 hover:text-white">{t("nav.government")}</Link></li>
                        <li><Link to="/resources" className="text-white/70 hover:text-white">{t("nav.resources")}</Link></li>
                        <li><Link to="/exportfy-ai" className="text-white/70 hover:text-white">{t("nav.ai")}</Link></li>
                        <li><Link to="/consultation" className="text-white/70 hover:text-white">{t("nav.consultation")}</Link></li>
                        <li><Link to="/contact" className="text-white/70 hover:text-white">{t("nav.contact")}</Link></li>
                    </ul>
                </div>
            </div>

            <div className="container-x mt-14">
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 grid sm:grid-cols-2 gap-6 items-center">
                    <div>
                        <div className="text-white font-bold text-lg">{t("footer.newsletterTitle")}</div>
                        <p className="text-sm text-white/60 mt-1">{t("footer.newsletterSubtitle")}</p>
                    </div>
                    <form onSubmit={submit} className="flex gap-2">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("footer.emailPlaceholder")}
                            data-testid={FORMS.newsletter.email}
                            className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid={FORMS.newsletter.submit}
                            className="rounded-full bg-white text-[#0A2540] px-5 py-3 text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
                        >
                            {loading ? "..." : t("footer.subscribe")}
                        </button>
                    </form>
                </div>
            </div>

            <div className="container-x mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
                <div>© {new Date().getFullYear()} Exportfy.world. {t("footer.copyright")}</div>
                <div className="flex items-center gap-5">
                    <Link to="/privacy" className="hover:text-white">{t("footer.privacy")}</Link>
                    <Link to="/terms" className="hover:text-white">{t("footer.terms")}</Link>
                </div>
            </div>
        </footer>
    );
}
